<template>
  <div ref="root" class="date-range-picker" :class="{ 'is-open': opened, 'is-disabled': disabled }">
    <button ref="trigger" type="button" class="date-range-trigger" :disabled="disabled" :aria-label="`${label}：${start || '开始日期'}至${end || '结束日期'}`" aria-haspopup="dialog" :aria-expanded="opened" :aria-controls="panelId" @click="toggle" @keydown.down.prevent="open" @keydown.esc.stop.prevent="close(true)">
      <span :class="{ placeholder: !start }">{{ start || '开始日期' }}</span><span class="date-range-separator" aria-hidden="true">-</span><span :class="{ placeholder: !end }">{{ end || '结束日期' }}</span>
      <svg class="date-range-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M7 2v6M17 2v6M3 10h18"/></svg>
    </button>
    <button v-if="(start || end) && !disabled" type="button" class="date-range-clear" :aria-label="`清空${label}`" @click="clear">×</button>
    <Teleport to="body">
      <section v-if="opened" :id="panelId" ref="panel" class="date-range-popover" :class="{ 'is-compact': compact }" :style="panelPosition" role="dialog" :aria-label="`选择${label}`" @keydown="onPanelKeydown">
        <nav class="date-range-shortcuts" aria-label="快捷日期范围"><button v-for="shortcut in normalizedShortcuts" :key="shortcut.days" type="button" :disabled="shortcutDisabled(shortcut.days)" :class="{ active: shortcutActive(shortcut.days) }" @click="chooseShortcut(shortcut.days)">{{ shortcut.label }}</button></nav>
        <div class="date-range-calendar-area">
          <div class="date-range-calendars">
            <section v-for="(month, index) in months" :key="month" class="date-range-month" :aria-label="month">
              <header class="date-range-month-header">
                <div class="date-range-month-controls"><template v-if="index === 0"><button type="button" aria-label="上一年" @click="navigateMonth(-12)">«</button><button type="button" aria-label="上个月" @click="navigateMonth(-1)">‹</button></template></div>
                <strong>{{ month }}</strong>
                <div class="date-range-month-controls"><template v-if="index === months.length - 1"><button type="button" aria-label="下个月" @click="navigateMonth(1)">›</button><button type="button" aria-label="下一年" @click="navigateMonth(12)">»</button></template></div>
              </header>
              <div class="date-range-weekdays" aria-hidden="true"><span v-for="day in weekdays" :key="day">{{ day }}</span></div>
              <div class="date-range-days">
                <button v-for="day in pickerMonthDays(month)" :key="day.value" type="button" :data-date="day.value" :data-current="day.current" :aria-label="day.value" :aria-pressed="day.value === draftStart || day.value === draftEnd" :aria-current="day.value === today ? 'date' : undefined" :disabled="pickerDateDisabled(day.value, min, max)" :tabindex="day.current && day.value === focusedDate ? 0 : -1" :class="dayClasses(day)" @click="chooseDay(day.value)" @mouseenter="hoveredDate = day.value" @focus="focusedDate = day.value">{{ day.day }}</button>
              </div>
            </section>
          </div>
          <p class="date-range-hint" aria-live="polite">{{ selectingEnd ? '请选择结束日期' : '请选择开始日期，或使用左侧快捷范围' }}</p>
        </div>
      </section>
    </Teleport>
  </div>
</template>
<script>
let pickerSequence = 0
</script>
<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { dateInputValue } from '../utils/recordQueries.js'
import { movePickerDate, movePickerDateMonth, orderedPickerRange, parsePickerDate, pickerDateDisabled, pickerMonth, pickerMonthDays, pickerShortcutRange, shiftPickerMonth } from '../utils/dateRangePicker.js'

const props = defineProps({
  start: { type: String, default: '' },
  end: { type: String, default: '' },
  min: { type: String, default: '' },
  max: { type: String, default: '' },
  label: { type: String, default: '日期范围' },
  disabled: Boolean,
  shortcuts: { type: Array, default: () => [1, -1, 7, 30, 90, 180, 360] },
})
const emit = defineEmits(['update:start', 'update:end', 'change', 'shortcut'])
const root = ref(null), trigger = ref(null), panel = ref(null), opened = ref(false), compact = ref(false)
const draftStart = ref(''), draftEnd = ref(''), selectingEnd = ref(false), hoveredDate = ref(''), focusedDate = ref('')
const anchorMonth = ref(pickerMonth(dateInputValue())), panelPosition = ref({})
const panelId = `date-range-panel-${++pickerSequence}`
const weekdays = ['日', '一', '二', '三', '四', '五', '六']
const today = dateInputValue()
const months = computed(() => compact.value ? [anchorMonth.value] : [anchorMonth.value, shiftPickerMonth(anchorMonth.value, 1)])
const normalizedShortcuts = computed(() => props.shortcuts.map(item => typeof item === 'number' ? { days: item, label: item === 1 ? '今日' : item === -1 ? '昨日' : `${item}日内` } : item))
const preview = computed(() => selectingEnd.value && hoveredDate.value && !pickerDateDisabled(hoveredDate.value, props.min, props.max)
  ? orderedPickerRange(draftStart.value, hoveredDate.value) : { start: draftStart.value, end: draftEnd.value })
function dayClasses(day) {
  return {
    'outside-month': !day.current,
    'is-today': day.value === today,
    'is-start': day.value === preview.value.start,
    'is-end': day.value === preview.value.end,
    'in-range': preview.value.start && preview.value.end && day.value >= preview.value.start && day.value <= preview.value.end,
  }
}
function initialDate() {
  let value = parsePickerDate(props.start) ? props.start : today
  if (props.min && value < props.min) value = props.min
  if (props.max && value > props.max) value = props.max
  return value
}
function positionPanel() {
  if (!opened.value || !trigger.value || !panel.value) return
  compact.value = window.innerWidth < 680
  const target = trigger.value.getBoundingClientRect(), popup = panel.value.getBoundingClientRect()
  const left = Math.max(12, Math.min(target.left, window.innerWidth - popup.width - 12))
  let top = target.bottom + 8
  if (top + popup.height > window.innerHeight - 12) top = Math.max(12, target.top - popup.height - 8)
  panelPosition.value = { left: `${left}px`, top: `${top}px` }
}
async function focusDate(value) {
  focusedDate.value = value
  await nextTick()
  panel.value?.querySelector(`[data-date="${value}"][data-current="true"]`)?.focus()
}
async function open() {
  if (props.disabled || opened.value) return
  compact.value = window.innerWidth < 680
  draftStart.value = props.start; draftEnd.value = props.end; selectingEnd.value = false; hoveredDate.value = ''
  const value = initialDate()
  focusedDate.value = value; anchorMonth.value = pickerMonth(value); opened.value = true
  await nextTick(); positionPanel(); await focusDate(value)
}
function close(restoreFocus = false) {
  opened.value = false
  if (restoreFocus) trigger.value?.focus()
}
function toggle() { if (opened.value) close(); else void open() }
function commit(range, days) {
  emit('update:start', range.start); emit('update:end', range.end); emit('change', range)
  if (days !== undefined) emit('shortcut', days)
  close(true)
}
function clear() { commit({ start: '', end: '' }) }
function chooseDay(value) {
  if (pickerDateDisabled(value, props.min, props.max)) return
  if (!selectingEnd.value) {
    draftStart.value = value; draftEnd.value = ''; selectingEnd.value = true; hoveredDate.value = value
  } else commit(orderedPickerRange(draftStart.value, value))
}
function shortcutDisabled(days) {
  const range = pickerShortcutRange(days)
  return pickerDateDisabled(range.start, props.min, props.max) || pickerDateDisabled(range.end, props.min, props.max)
}
function shortcutActive(days) {
  const range = pickerShortcutRange(days)
  return props.start === range.start && props.end === range.end
}
function chooseShortcut(days) {
  if (!shortcutDisabled(days)) commit(pickerShortcutRange(days), days)
}
function navigateMonth(offset) {
  anchorMonth.value = shiftPickerMonth(anchorMonth.value, offset)
  let value = `${anchorMonth.value}-01`
  if (props.min && pickerMonth(props.min) === anchorMonth.value && value < props.min) value = props.min
  if (props.max && pickerMonth(props.max) === anchorMonth.value && value > props.max) value = props.max
  focusedDate.value = value
}
function onOutside(event) {
  if (opened.value && !root.value?.contains(event.target) && !panel.value?.contains(event.target)) close()
}
function onPanelKeydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return }
  const button = event.target.closest('[data-date]')
  if (!button) return
  const value = button.dataset.date
  let target
  const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }
  if (event.key in moves) target = movePickerDate(value, moves[event.key])
  if (event.key === 'Home') target = movePickerDate(value, -parsePickerDate(value).getDay())
  if (event.key === 'End') target = movePickerDate(value, 6 - parsePickerDate(value).getDay())
  if (event.key === 'PageUp' || event.key === 'PageDown') target = movePickerDateMonth(value, (event.key === 'PageUp' ? -1 : 1) * (event.shiftKey ? 12 : 1))
  if (!target) return
  event.preventDefault()
  if (props.min && target < props.min) target = props.min
  if (props.max && target > props.max) target = props.max
  if (!months.value.includes(pickerMonth(target))) anchorMonth.value = pickerMonth(target)
  void focusDate(target)
}
function onResize() { compact.value = window.innerWidth < 680; void nextTick(positionPanel) }
watch(() => props.disabled, value => { if (value) close() })
onMounted(() => {
  document.addEventListener('pointerdown', onOutside, true)
  document.addEventListener('focusin', onOutside)
  window.addEventListener('resize', onResize)
  window.addEventListener('scroll', positionPanel, true)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onOutside, true)
  document.removeEventListener('focusin', onOutside)
  window.removeEventListener('resize', onResize)
  window.removeEventListener('scroll', positionPanel, true)
})
</script>
<style scoped>
.date-range-picker { position:relative; width:310px; max-width:100%; min-width:0; }
.date-range-trigger { display:flex; align-items:center; gap:10px; width:100%; min-height:44px; padding:10px 12px; border:1px solid #dce5f0; border-radius:8px; background:#f7f9fc; color:#435570; font-size:14px; white-space:nowrap; }
.date-range-trigger > span:not(.date-range-separator) { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; text-align:left; }
.date-range-trigger .placeholder { color:#8c98aa; }
.date-range-separator { color:#9aa5b4; }
.date-range-icon { flex:none; margin-left:20px; color:#718099; }
.date-range-trigger:hover,.is-open .date-range-trigger { border-color:#419ff0; }
.date-range-trigger:focus-visible { outline:2px solid #419ff0; outline-offset:2px; }
.date-range-clear { position:absolute; right:36px; top:50%; transform:translateY(-50%); width:20px; height:24px; padding:0; background:transparent; color:#8795a9; font-size:18px; }
.date-range-clear:hover { color:#1687d5; }
.date-range-popover { position:fixed; z-index:110; display:flex; width:730px; max-width:calc(100vw - 24px); max-height:calc(100dvh - 24px); overflow:auto; border:1px solid #e2e8f2; border-radius:10px; background:#fff; box-shadow:0 8px 28px rgb(39 67 105 / 16%); color:#334158; font-size:14px; }
.date-range-shortcuts { flex:0 0 108px; display:flex; flex-direction:column; gap:10px; padding:16px 12px; border-right:1px solid #e8edf5; }
.date-range-shortcuts button { min-height:34px; padding:7px 6px; border-radius:5px; background:#f3f5f8; color:#64738a; white-space:nowrap; }
.date-range-shortcuts button:hover:not(:disabled),.date-range-shortcuts button.active { background:#eaf5ff; color:#1486d3; }
.date-range-shortcuts button:disabled { opacity:.4; cursor:not-allowed; }
.date-range-calendar-area { flex:1; min-width:0; padding:0 10px 10px; }
.date-range-calendars { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); }
.date-range-month { min-width:0; padding:0 6px; }
.date-range-month + .date-range-month { border-left:1px solid #f0f3f8; }
.date-range-month-header { display:grid; grid-template-columns:64px 1fr 64px; align-items:center; height:56px; border-bottom:1px solid #edf1f7; }
.date-range-month-header strong { text-align:center; font-weight:500; white-space:nowrap; }
.date-range-month-controls { display:flex; justify-content:center; }
.date-range-month-controls button { width:30px; height:30px; padding:0; border-radius:5px; background:transparent; color:#738198; font-size:22px; }
.date-range-month-controls button:hover { color:#1687d5; background:#eff7ff; }
.date-range-weekdays,.date-range-days { display:grid; grid-template-columns:repeat(7,minmax(0,1fr)); text-align:center; }
.date-range-weekdays { padding-top:10px; color:#8d96a5; font-size:12px; }
.date-range-weekdays span { padding:8px 0; }
.date-range-days { row-gap:5px; }
.date-range-days button { position:relative; height:32px; min-width:0; padding:0; border-radius:4px; background:transparent; color:#34445c; font-size:13px; }
.date-range-days button.outside-month { color:#b8c0cd; }
.date-range-days button:disabled { color:#ccd2dc; cursor:not-allowed; }
.date-range-days button.in-range { background:#eaf4ff; border-radius:0; }
.date-range-days button.is-start,.date-range-days button.is-end { border-radius:5px; background:#2698ed; color:#fff; }
.date-range-days button:hover:not(:disabled):not(.is-start):not(.is-end) { background:#e0f0ff; color:#1687d5; }
.date-range-days button:focus-visible { outline:2px solid #1687d5; outline-offset:-2px; }
.date-range-days button.is-today::after { position:absolute; bottom:2px; left:calc(50% - 2px); width:4px; height:4px; border-radius:50%; background:#2698ed; content:''; }
.date-range-days button.is-today.is-start::after,.date-range-days button.is-today.is-end::after { background:#fff; }
.date-range-hint { margin:12px 6px 0; padding-top:10px; border-top:1px solid #edf1f7; color:#8a98ac; font-size:12px; }
.is-compact { width:430px; }
.is-compact .date-range-shortcuts { flex-basis:82px; padding:12px 8px; gap:8px; }
.is-compact .date-range-calendar-area { padding:0 6px 10px; }
.is-compact .date-range-calendars { grid-template-columns:minmax(0,1fr); }
.is-compact .date-range-month-header { grid-template-columns:56px 1fr 56px; height:48px; }
.is-compact .date-range-month-controls button { width:28px; }
.is-compact .date-range-month { padding:0; }
.is-compact .date-range-days button { height:32px; font-size:12px; }
@media(max-width:767px) { .date-range-picker { width:100%; } }
</style>
